import { DatePipe, NgFor, NgIf, NgTemplateOutlet } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { AppService } from 'app/services/app/app.service';
import { FormsModule } from '@angular/forms';
import { FieldFilter, FilterModel } from 'app/models/filter-model';
import { SharedModule } from 'app/shared.module';
import { MatSidenav } from '@angular/material/sidenav';
import { Observable } from 'rxjs';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { map, shareReplay } from 'rxjs/operators';
import { Log } from 'app/core/logging/log';
import { CategoryFlat, CategorySelectorComponent } from '../category-selector/category-selector.component';
import { UserModel } from 'app/models/user';


export interface MassiveActions {
  key: string;
  enabled: boolean;
};


@Component({
  selector: 'app-filter',
  templateUrl: './filter.component.html',
  standalone: true,
  imports: [SharedModule, NgIf, NgTemplateOutlet, NgFor, FormsModule, CategorySelectorComponent],
  styleUrls: ['./filter.component.scss'],
  providers: [DatePipe]
})
export class FilterComponent implements OnInit {

  @Input() filters: FieldFilter[] = [];
  @Input() accions: string[] = [];
  @Input() massiveAccions: MassiveActions;
  @Input() loading = false;
  @Input() summaryBadgeText = '';
  @Output('submit') submitClicked: EventEmitter<string> = new EventEmitter();
  @Output('massiveAction') massiveActionClicked: EventEmitter<string> = new EventEmitter();
  @Output('massiveActionChanged') massiveActionChanged: EventEmitter<string> = new EventEmitter();
  @Input() btLabel: string = "Buscar";

  @ViewChild('sidenav') sidenav!: MatSidenav;
  isMobile$!: Observable<boolean>;
  showOverlay = false;
  showAdvancedFilters = true;
  activeFiltersCount = 0;
  visibleFilters: FieldFilter[] = [];
  activeFilterChips: { key: string; label: string; value: string }[] = [];
  hasSecondaryFilters = false;


  isMobile: boolean = false;
  width: number = window.innerWidth;
  height: number = window.innerHeight;
  mobileWidth: number = 760;

  constructor(public appService: AppService, private breakpoint: BreakpointObserver, private datePipe: DatePipe) {

  }

  ngOnInit(): void {

    this.isMobile$ = this.breakpoint.observe([Breakpoints.Handset])
      .pipe(
        map(result => result.matches),
        shareReplay()
      );


    this.isMobile = this.width < this.mobileWidth;
    this.showAdvancedFilters = !this.isMobile;
    this.refreshDerivedState();

    //this.isMobile$ = of(this.isMobile);
  }

  onWindowResize(event) {
    this.width = event.target.innerWidth;
    this.height = event.target.innerHeight;
    this.isMobile = this.width < this.mobileWidth;
    this.showAdvancedFilters = !this.isMobile;
    this.refreshDerivedState();
    //this.isMobile$ = of(this.isMobile);
    Log.info('this.isMobile', {isMobile:this.isMobile});
  }

  onClear() {
    this.filters.forEach(f => {
      f.value = '';
      f.values = [];
      f.selectedUser = null;
    });
    this.refreshDerivedState();
  }

  abrirOverlay() {
    this.showOverlay = true;
  }

  cerrarOverlay() {
    this.showOverlay = false;
  }

  submit(accion: string = null) {
    if (this.loading) {
      return;
    }
    Log.info('filters::submit', {filters:this.filters, accion});
    this.submitClicked.next(accion);
    this.showOverlay = false; // cerrar overlay en móviles
  }

  onClickMassiveAction(accion: string = null) {
    if (this.loading) {
      return;
    }
    this.massiveActionClicked.next(accion);
    this.showOverlay = false; // cerrar overlay en móviles
  }

  onDateChange(name: string, index: number, event: any) {
    const fechaFormateada = this.datePipe.transform(event.value, 'yyyy-MM-dd');
    Log.info('onDateChange', {fechaFormateada, name});
    this.filters.find(f => f.name == name).values[index] = fechaFormateada;
    this.refreshDerivedState();
  }

  onSelectChange(filter: FilterModel) {
    if (Array.isArray(filter.value)) {
      filter.values = [...filter.value];
    } else {
      filter.values = filter.value != null && filter.value !== '' ? [filter.value] : [];
    }
    this.refreshDerivedState();
  }

  categoryOnChangeValue(filter: FilterModel, value:CategoryFlat){
    Log.info('categoryOnChangeValue', {filter: filter, value:value});
    filter.value = value?.id != null ? String(value.id) : '';
    filter.values = filter.value ? [filter.value] : [];
    Log.info('categoryOnChangeValue changed', {filter: filter, value:value?.id});
    this.refreshDerivedState();
  }

  userOnChangeValue(filter: FieldFilter, user: UserModel | null) {
    filter.selectedUser = user;
    filter.value = user?.id != null ? String(user.id) : '';
    filter.values = filter.value ? [filter.value] : [];
    this.refreshDerivedState();
  }

  onChangeMassiveAction(value: MassiveActions) {
    Log.info('onChangeMassiveAction', {value});
    this.massiveActionChanged.next(value.key);
  }

  isOption(option: any): boolean {
    return (typeof option === 'object' && option !== null && option.hasOwnProperty('value') && option.hasOwnProperty('label'));
  }

  onFilterValueChange() {
    this.refreshDerivedState();
  }

  toggleAdvancedFilters() {
    this.showAdvancedFilters = !this.showAdvancedFilters;
    this.refreshDerivedState();
  }

  trackByFilterName(index: number, filter: FieldFilter): string {
    return filter.name;
  }

  trackByChipKey(index: number, chip: { key: string }): string {
    return chip.key;
  }

  private refreshDerivedState() {
    this.activeFiltersCount = this.filters.filter(filter => this.hasFilterValue(filter)).length;
    this.visibleFilters = (this.showAdvancedFilters || this.filters.length <= 4)
      ? this.filters
      : this.filters.slice(0, 4);
    this.activeFilterChips = this.filters
      .filter(filter => this.hasFilterValue(filter))
      .map(filter => ({
        key: filter.name,
        label: filter.label,
        value: this.getFilterDisplayValue(filter)
      }));
    this.hasSecondaryFilters = this.filters.length > 4;
  }

  getFieldIcon(filter: FieldFilter): string {
    const name = filter.name?.toLowerCase?.() || '';

    if (filter.type === 'date' || filter.type === 'datetime' || filter.type === 'time') {
      return 'calendar_today';
    }

    if (filter.type === 'category' || name.includes('category')) {
      return 'grid_view';
    }

    if (name.includes('status')) {
      return 'flag';
    }

    if (name.includes('type') || name.includes('ticket')) {
      return 'confirmation_number';
    }

    if (name.includes('customer') || name.includes('client') || name.includes('user')) {
      return 'person_outline';
    }

    if (name.includes('telephone') || name.includes('phone')) {
      return 'call';
    }

    if (name.includes('email')) {
      return 'mail_outline';
    }

    if (name.includes('invoice')) {
      return 'description';
    }

    return 'tune';
  }

  private hasFilterValue(filter: FieldFilter): boolean {
    return (filter.value != null && filter.value !== '') || (filter.values != null && filter.values.length > 0);
  }

  private getFilterDisplayValue(filter: FieldFilter): string {
    if (filter.type === 'date' || filter.type === 'datetime' || filter.type === 'time') {
      return filter.values?.filter(Boolean)?.join(' - ') || '';
    }

    if (filter.type === 'select' && filter.multiple) {
      return filter.values?.filter(Boolean)?.join(', ') || '';
    }

    if (filter.type === 'select' && filter.options?.length) {
      const selected = filter.options.find(option => this.isOption(option) ? option.value === filter.value : option === filter.value);
      if (this.isOption(selected)) {
        return selected.label;
      }
    }

    if (filter.type === 'category') {
      return filter.value || '';
    }

    if (filter.type === 'user-search') {
      if (!filter.selectedUser) {
        return '';
      }
      return filter.selectedUser.username || filter.selectedUser.email || String(filter.selectedUser.id || '');
    }

    if (filter.values?.length) {
      return filter.values.filter(Boolean).join(', ');
    }

    return filter.value || '';
  }

}

